import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Pencil, Plus, Trash2, Search, Sprout, Repeat, Heart, AlertTriangle,
  CalendarClock, Sparkles, X, Check, Move, Wand2, Ruler, LayoutGrid, Eraser, Scissors,
} from 'lucide-react';
import ContinuousHarvestPanel from '@/components/ContinuousHarvest';
import { recognizeShape, strokePath, fallbackLayout, layoutsNeedTidy, tidyCell } from './shapeUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAMILY_COLORS = {
  Brassicaceae: '#6B9F4A', Amaranthaceae: '#3F8E6E', Fabaceae: '#93B84A',
  Solanaceae: '#C66B4A', Cucurbitaceae: '#C9A85B', Apiaceae: '#8FB4D6',
  Alliaceae: '#A97BB0', Amaryllidaceae: '#A97BB0', Asteraceae: '#7DA24A',
  Lamiaceae: '#5B9B8C', Poaceae: '#B8A24A', Rosaceae: '#C98BA0',
};
const FALLBACK = ['#6B9F4A', '#C66B4A', '#8FB4D6', '#C9A85B', '#3F8E6E', '#A97BB0', '#5B9B8C', '#B8A24A'];
const ROT_COLOR = { red: '#C66B4A', orange: '#C9A85B', green: '#6B9F4A', neutral: '#8EAA97' };
const ROT_LABEL = { red: 'Rotation clash', orange: 'A bit soon', green: 'Great rotation', neutral: 'OK to plant here' };
const SEASON_GLOW = { peak: '#6B9F4A', shoulder: '#C9A85B', out: '#8EAA97' };

function colorFor(crop) {
  const fam = crop.family;
  if (fam && FAMILY_COLORS[fam]) return FAMILY_COLORS[fam];
  const key = crop.crop_id || crop.crop_name || '';
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 997;
  return FALLBACK[h % FALLBACK.length];
}

function cnorm(s) { return (s || '').replace(/\(.*?\)/g, '').trim().toLowerCase().replace(/s$/, ''); }
function entryMatches(entry, placed) {
  if (entry.crop_id && entry.crop_id === placed.crop_id) return true;
  const a = cnorm(entry.name), b = cnorm(placed.crop_name);
  return !!a && !!b && (a === b || a.includes(b) || b.includes(a));
}
function companionSummary(companionMap, cropId, bedCrops) {
  const doc = cropId ? companionMap[cropId] : null;
  const friends = [], foes = [];
  if (!doc) return { friends, foes };
  for (const c of bedCrops || []) {
    for (const f of (doc.foes || [])) if (entryMatches(f, c)) { foes.push({ name: c.crop_name, why: f.why }); break; }
    for (const f of (doc.friends || [])) if (entryMatches(f, c)) { friends.push({ name: c.crop_name, why: f.why }); break; }
  }
  return { friends, foes };
}

// ---------- a single bed shape on the canvas ----------
function BedShape({ bed, layout, selected, hovered, verdictColor, tool, onBedPointerDown, registerRef }) {
  const dominant = bed.crops?.[0] ? colorFor(bed.crops[0]) : '#8EAA97';
  const totalCount = (bed.crops || []).length;
  const radius = layout.shape === 'circle' ? '50%' : '24px';
  const ringColor = hovered && verdictColor ? verdictColor : selected ? '#1A4331' : null;

  return (
    <motion.div
      ref={(el) => registerRef(bed.bed_id, el)}
      data-testid={`canvas-bed-${bed.bed_id}`}
      initial={{ scale: 0.4, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      onPointerDown={(e) => { if (tool === 'select') { e.stopPropagation(); onBedPointerDown(bed, e); } }}
      style={{
        position: 'absolute',
        left: `${layout.x * 100}%`, top: `${layout.y * 100}%`,
        width: `${layout.w * 100}%`, height: `${layout.h * 100}%`,
        borderRadius: radius,
        background: `linear-gradient(150deg, ${dominant}24 0%, ${dominant}12 55%, rgba(255,255,255,0.72) 100%)`,
        border: `1.5px solid ${ringColor || dominant + '59'}`,
        boxShadow: ringColor
          ? `0 0 0 4px ${ringColor}33, 0 22px 48px -20px ${dominant}99`
          : `0 16px 38px -22px ${dominant}99, inset 0 1px 0 rgba(255,255,255,0.7)`,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        cursor: tool === 'select' ? 'grab' : 'crosshair',
        overflow: 'hidden',
        transition: 'border-color .18s, box-shadow .18s',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, padding: '12px 14px', display: 'flex', flexDirection: 'column' }}>
        <div className="flex items-center justify-between gap-1" style={{ pointerEvents: 'none' }}>
          <span className="text-sm font-bold truncate" style={{ color: '#1A4331', fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>{bed.name}</span>
          {totalCount > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#1A433118', color: '#1A4331' }}>{totalCount}</span>
          )}
        </div>

        {totalCount === 0 ? (
          <div className="flex-1 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
            <motion.div
              animate={{ scale: [1, 1.14, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              className="flex items-center justify-center"
              style={{ width: 44, height: 44, borderRadius: 22, background: 'rgba(255,253,247,0.95)', border: '1px solid rgba(26,67,49,0.08)', boxShadow: '0 4px 14px rgba(26,67,49,0.14)' }}
            >
              <Plus className="w-5 h-5" style={{ color: '#8EAA97' }} />
            </motion.div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 mt-2 overflow-hidden content-start" style={{ pointerEvents: 'none' }}>
            {(bed.crops || []).slice(0, 7).map((c) => (
              <span key={c.planting_id} className="inline-flex items-center gap-1 text-[11px] font-bold text-white pl-1 pr-2 py-1 rounded-full shadow-sm"
                style={{ backgroundColor: colorFor(c) }}>
                <span className="flex items-center justify-center rounded-full" style={{ width: 16, height: 16, background: 'rgba(255,255,255,0.28)' }}>
                  <Sprout className="w-2.5 h-2.5 text-white" />
                </span>
                {c.crop_name}{c.plant_count ? ` ×${c.plant_count}` : ''}
              </span>
            ))}
            {(bed.crops || []).length > 7 && (
              <span className="text-[11px] font-bold px-2 py-1 rounded-full" style={{ background: '#1A433118', color: '#1A4331' }}>+{bed.crops.length - 7}</span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ---------- the 3-point live advice while carrying ----------
function CarryAdvice({ crop, hoverBed, rotVerdict, companionMap, season, nowMonth }) {
  if (!hoverBed) {
    return (
      <div className="text-center py-8 px-2">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: '#B4D33022' }}>
          <Move className="w-7 h-7" style={{ color: '#6B9F4A' }} />
        </div>
        <p className="text-sm font-semibold text-[#1A4331]">Carrying {crop.crop_name}</p>
        <p className="text-xs text-[#7A6B57] mt-1">Drag it over a bed to see the live rotation, companion &amp; season read.</p>
      </div>
    );
  }
  const rv = rotVerdict || { level: 'neutral', reason: 'No rotation history for this bed yet — a safe start.' };
  const comp = companionSummary(companionMap, crop.crop_id, hoverBed.crops);
  const sowable = season && (season.year_round || (season.viable_months || []).includes(nowMonth));
  const compLevel = comp.foes.length ? 'red' : comp.friends.length ? 'green' : 'neutral';
  const seasonLevel = !season ? 'neutral' : sowable ? 'green' : 'orange';

  const Row = ({ icon: Icon, level, title, body, testid }) => (
    <div className="rounded-2xl p-3 mb-2" style={{ background: `${ROT_COLOR[level]}12` }} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: ROT_COLOR[level] }} />
        <span className="text-xs font-bold" style={{ color: ROT_COLOR[level] }}>{title}</span>
      </div>
      <p className="text-[11px] text-[#5C665C] leading-snug mt-1">{body}</p>
    </div>
  );

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#7A6B57] mb-2">Placing into {hoverBed.name}</p>
      <Row icon={Repeat} level={rv.level} title={ROT_LABEL[rv.level] || 'Rotation'} body={rv.reason} testid="advice-rotation" />
      <Row icon={comp.foes.length ? AlertTriangle : Heart} level={compLevel}
        title={comp.foes.length ? 'Companion clash' : comp.friends.length ? 'Good companions' : 'Companions'}
        body={comp.foes.length ? `Keep apart from ${comp.foes.map((f) => f.name).join(', ')} — ${comp.foes[0].why}`
          : comp.friends.length ? `Loves growing near ${comp.friends.map((f) => f.name).join(', ')} — ${comp.friends[0].why}`
            : 'No strong friends or foes among this bed\u2019s crops.'}
        testid="advice-companion" />
      <Row icon={CalendarClock} level={seasonLevel}
        title={!season ? 'Season' : sowable ? 'In season now' : 'Out of season'}
        body={!season ? 'Checking your regional window…'
          : sowable ? `Great timing — ${crop.crop_name} can be ${season.verb || 'sown'} now in your ${season.zone_label || 'region'}.`
            : `Best ${season.verb || 'sown'} in ${season.suggested_range || 'its window'} for your ${season.zone_label || 'region'}. It's a bit off-season right now.`}
        testid="advice-season" />
    </div>
  );
}

// ---------- floating control button ----------
function ClusterBtn({ icon: Icon, label, onClick, active, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group flex flex-col items-center justify-center gap-0.5 rounded-2xl transition-all active:scale-95"
      style={{
        width: 60, height: 60,
        background: active ? 'rgba(26,67,49,0.92)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid ${active ? 'rgba(26,67,49,0.4)' : 'rgba(255,255,255,0.6)'}`,
        boxShadow: active ? '0 10px 26px -10px rgba(26,67,49,0.6)' : '0 8px 24px -12px rgba(26,67,49,0.3)',
        color: active ? '#FFFFFF' : '#1A4331',
      }}>
      <Icon className="w-5 h-5" />
      <span className="text-[9px] font-bold uppercase tracking-wide">{label}</span>
    </button>
  );
}

function fitsLine(fits) {
  return (fits || []).slice(0, 2).map((f) => `~${f.count} ${f.crop_name}`).join(' · ');
}

function BedSizeBlock({ bed, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(bed.size || '');
  const [preview, setPreview] = useState(null);
  const tRef = useRef(null);

  useEffect(() => { setValue(bed.size || ''); setEditing(false); }, [bed.size, bed.bed_id]);

  useEffect(() => {
    if (!editing) return undefined;
    if (tRef.current) clearTimeout(tRef.current);
    const v = value.trim();
    if (!v) { setPreview(null); return undefined; }
    tRef.current = setTimeout(() => {
      axios.get(`${API}/planner/parse-size`, { params: { size: v }, withCredentials: true })
        .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 300);
    return () => { if (tRef.current) clearTimeout(tRef.current); };
  }, [value, editing]);

  const save = () => { onSave((value || '').trim()); setEditing(false); };

  if (editing) {
    return (
      <div className="rounded-2xl p-3 mb-3" style={{ background: '#1A43310A' }} data-testid="bed-size-editor">
        <input autoFocus value={value} onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="e.g. 2m x 1m · 30cm pot · 7L bag · 3 rows @40cm" data-testid="bed-size-input"
          className="w-full px-2.5 py-2 bg-white rounded-lg outline-none text-sm border border-[#1A433122]" />
        {preview && preview.recognized ? (
          <div className="mt-2 text-[11px] text-[#1A4331]" data-testid="bed-size-preview">
            <span className="font-bold">{preview.label}</span>
            {preview.fits?.length ? <span className="text-[#5C665C]"> — fits {fitsLine(preview.fits)}</span> : null}
            {preview.assumptions?.length ? <div className="text-[10px] text-[#7A6B57] mt-0.5">{preview.assumptions.join(' · ')}</div> : null}
          </div>
        ) : value.trim() ? (
          <div className="mt-2 text-[11px] text-[#7A6B57]" data-testid="bed-size-preview">Type any size — e.g. “2m x 1m”, “30cm pot”, “7L bag” or “3 rows @40cm”.</div>
        ) : null}
        <div className="flex gap-1.5 mt-2">
          <button onClick={save} data-testid="bed-size-save" className="flex-1 py-1.5 rounded-lg bg-[#1A4331] text-white text-xs font-bold">Save size</button>
          <button onClick={() => setEditing(false)} data-testid="bed-size-cancel" className="px-3 py-1.5 rounded-lg bg-[#F0F2EB] text-[#1A4331] text-xs font-bold">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => setEditing(true)} data-testid="bed-size-open"
      className="w-full text-left rounded-2xl p-3 mb-3 hover:bg-[#1A43310F] transition-colors" style={{ background: '#1A433108' }}>
      <div className="flex items-center gap-2">
        <Ruler className="w-4 h-4 text-[#1A4331]" />
        <span className="text-xs font-bold text-[#1A4331]" data-testid="bed-size-label">{bed.size_label || bed.size || 'Add a size'}</span>
        <Pencil className="w-3 h-3 text-[#7A6B57] ml-auto" />
      </div>
      {bed.fits_examples?.length ? (
        <p className="text-[11px] text-[#5C665C] mt-1" data-testid="bed-size-fits">Fits {fitsLine(bed.fits_examples)}</p>
      ) : (
        <p className="text-[11px] text-[#7A6B57] mt-1">Tap to set size — pot, bag, rows or a plot.</p>
      )}
      {bed.fullness_pct != null ? (
        <div className="mt-2">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#1A433115' }}>
            <div className="h-full rounded-full" style={{ width: `${bed.fullness_pct}%`, background: bed.fullness_pct >= 100 ? '#C66B4A' : '#6B9F4A' }} />
          </div>
          <p className="text-[10px] text-[#7A6B57] mt-0.5" data-testid="bed-fullness">{bed.fullness_pct}% full</p>
        </div>
      ) : null}
      {bed.size_assumptions?.length ? <p className="text-[10px] text-[#7A6B57] mt-1">{bed.size_assumptions.join(' · ')}</p> : null}
    </button>
  );
}


export default function HolographicCanvas({
  garden, planner, companionMap, successionCrops, rotationBeds, rotCheck, aiOn,
  onCreateBed, onAssignCrop, onRemoveCrop, onRenameBed, onDeleteBed, onUpdateBedSize, onOpenHistory, onCarryStart, onCarryEnd, onPersistLayout, onAutoFill, onClearBed,
}) {
  const canvasRef = useRef(null);
  const bedRefs = useRef({});
  const navigate = useNavigate();
  const [tool, setTool] = useState('select');
  const [stroke, setStroke] = useState(null); // pixel points while drawing
  const [carry, setCarry] = useState(null); // {crop, x, y}
  const [hoverBedId, setHoverBedId] = useState(null);
  const [selectedBedId, setSelectedBedId] = useState(null);
  const [query, setQuery] = useState('');
  const [season, setSeason] = useState({}); // cropId -> window (live carry read)
  const [renaming, setRenaming] = useState(null);
  const [openSucc, setOpenSucc] = useState(null);
  const [layoutOverride, setLayoutOverride] = useState({}); // bedId -> live layout during drag
  const [seasonOpen, setSeasonOpen] = useState(true);
  const [tpl, setTpl] = useState(null); // regional planting template -> per-crop season state
  const [warn, setWarn] = useState(null); // biological warning strip {level,title,fix}
  const [filling, setFilling] = useState(false);
  const [fillMsg, setFillMsg] = useState(null); // transient auto-fill result banner
  const carryRef = useRef(null);
  const hoverRef = useRef(null);
  const bedDragRef = useRef(null);
  const warnTimer = useRef(null);
  const selectedBedIdRef = useRef(null);
  selectedBedIdRef.current = selectedBedId;

  const beds = planner?.beds || [];
  const unassigned = planner?.unassigned || [];
  const nowMonth = new Date().getMonth() + 1;

  const registerRef = useCallback((id, el) => { bedRefs.current[id] = el; }, []);

  // Regional season state for the season strip (peak / shoulder / out).
  useEffect(() => {
    axios.get(`${API}/planting-template`, { withCredentials: true })
      .then((r) => setTpl(r.data)).catch(() => {});
  }, []);
  const seasonState = useMemo(() => {
    const map = {};
    (tpl?.crops || []).forEach((c) => { map[c.crop_id] = c.marginal ? 'shoulder' : 'peak'; });
    return map;
  }, [tpl]);
  const cropSeason = (cropId) => seasonState[cropId] || 'out';

  const triggerWarn = useCallback((level, title, fix) => {
    setWarn({ level, title, fix });
    if (warnTimer.current) clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(() => setWarn(null), 5000);
  }, []);
  useEffect(() => () => { if (warnTimer.current) clearTimeout(warnTimer.current); }, []);

  // Give layout-less (legacy) beds a tidy slot, and auto-reflow an overlapping /
  // off-canvas garden into a clean non-overlapping grid (once, on load) so the
  // canvas never looks cluttered. Shapes are preserved; circles stay round (16:10).
  const currentLayout = (bed, idx) => layoutOverride[bed.bed_id] || bed.layout || fallbackLayout(idx);
  useEffect(() => {
    if (!onPersistLayout || beds.length === 0) return;
    const eff = beds.map((b, i) => b.layout || fallbackLayout(i));
    if (layoutsNeedTidy(eff)) {
      beds.forEach((b, i) => onPersistLayout(b.bed_id, tidyCell(i, beds.length, b.layout?.shape || 'rect', 1.6)));
    } else {
      beds.forEach((bed, idx) => { if (!bed.layout) onPersistLayout(bed.bed_id, fallbackLayout(idx)); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beds.length]);

  // ---- reposition a bed (Move tool) ----
  const onBedPointerDown = (bed, e) => {
    const idx = beds.findIndex((b) => b.bed_id === bed.bed_id);
    const rect = canvasRef.current.getBoundingClientRect();
    bedDragRef.current = { bedId: bed.bed_id, sx: e.clientX, sy: e.clientY, orig: currentLayout(bed, idx), last: null, rw: rect.width, rh: rect.height, moved: false };
    window.addEventListener('pointermove', onBedMove);
    window.addEventListener('pointerup', onBedUp);
  };
  const onBedMove = (e) => {
    const d = bedDragRef.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.sx) > 3 || Math.abs(e.clientY - d.sy) > 3) d.moved = true;
    const nx = Math.max(0.01, Math.min(0.99 - d.orig.w, d.orig.x + (e.clientX - d.sx) / d.rw));
    const ny = Math.max(0.01, Math.min(0.99 - d.orig.h, d.orig.y + (e.clientY - d.sy) / d.rh));
    d.last = { ...d.orig, x: nx, y: ny };
    setLayoutOverride((o) => ({ ...o, [d.bedId]: d.last }));
  };
  const onBedUp = () => {
    window.removeEventListener('pointermove', onBedMove);
    window.removeEventListener('pointerup', onBedUp);
    const d = bedDragRef.current;
    bedDragRef.current = null;
    if (!d) return;
    if (d.moved && d.last) onPersistLayout(d.bedId, d.last);
    else setSelectedBedId(d.bedId);
  };

  const canvasPoint = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // ---- freehand drawing ----
  const onCanvasPointerDown = (e) => {
    if (tool !== 'draw') { setSelectedBedId(null); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    setStroke([canvasPoint(e)]);
  };
  const onCanvasPointerMove = (e) => {
    if (tool !== 'draw' || !stroke) return;
    setStroke((s) => [...s, canvasPoint(e)]);
  };
  const onCanvasPointerUp = () => {
    if (tool !== 'draw' || !stroke) return;
    const r = canvasRef.current.getBoundingClientRect();
    const layout = recognizeShape(stroke, r.width, r.height);
    setStroke(null);
    if (layout) { onCreateBed(layout); setTool('select'); }
  };

  const quickAddBed = () => { onCreateBed(fallbackLayout(beds.length)); setTool('select'); };

  // ---- crop carry (pointer drag from season strip) ----
  const fetchSeason = (cropId) => {
    if (season[cropId]) return;
    axios.get(`${API}/season-plan/crop-window?crop_id=${cropId}&method=seed`, { withCredentials: true })
      .then((res) => setSeason((s) => ({ ...s, [cropId]: res.data })))
      .catch(() => {});
  };

  const startCarry = (crop, e) => {
    e.preventDefault();
    carryRef.current = crop;
    setCarry({ crop, x: e.clientX, y: e.clientY });
    onCarryStart && onCarryStart(crop.crop_id);
    fetchSeason(crop.crop_id);
    window.addEventListener('pointermove', onCarryMove);
    window.addEventListener('pointerup', onCarryUp);
  };
  const onCarryMove = (e) => {
    setCarry((c) => (c ? { ...c, x: e.clientX, y: e.clientY } : c));
    let found = null;
    for (const bed of beds) {
      const el = bedRefs.current[bed.bed_id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) { found = bed.bed_id; break; }
    }
    hoverRef.current = found;
    setHoverBedId(found);
  };
  const onCarryUp = () => {
    window.removeEventListener('pointermove', onCarryMove);
    window.removeEventListener('pointerup', onCarryUp);
    const crop = carryRef.current;
    const bedId = hoverRef.current;
    if (crop && bedId) onAssignCrop(crop.planting_id, bedId);
    else if (crop && !bedId) {
      // Tap (no drag onto a bed) = plant into the selected bed.
      if (selectedBedIdRef.current) onAssignCrop(crop.planting_id, selectedBedIdRef.current);
      else triggerWarn('amber', 'Pick a bed first', 'Tap a bed on the canvas, then tap a crop to plant it there.');
    }
    carryRef.current = null; hoverRef.current = null;
    setCarry(null); setHoverBedId(null);
    onCarryEnd && onCarryEnd();
  };
  useEffect(() => () => {
    window.removeEventListener('pointermove', onCarryMove);
    window.removeEventListener('pointerup', onCarryUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUnassigned = unassigned.filter((c) => c.crop_name.toLowerCase().includes(query.trim().toLowerCase()));
  const sortedStrip = useMemo(() => {
    const rank = { peak: 0, shoulder: 1, out: 2 };
    return [...filteredUnassigned].sort((a, b) => rank[cropSeason(a.crop_id)] - rank[cropSeason(b.crop_id)] || a.crop_name.localeCompare(b.crop_name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUnassigned, seasonState]);

  const selectedBed = beds.find((b) => b.bed_id === selectedBedId);
  const rotB = selectedBed ? rotationBeds[selectedBed.bed_id] : null;
  const carryHoverBed = carry ? beds.find((b) => b.bed_id === hoverBedId) : null;
  const showPanel = !!carry || !!selectedBed;

  // ---- biological warning strip: fire on a live planting conflict while carrying ----
  const carryCropId = carry?.crop?.crop_id;
  useEffect(() => {
    if (!carry || !carryHoverBed) return;
    const comp = companionSummary(companionMap, carry.crop.crop_id, carryHoverBed.crops);
    const rv = rotCheck[carryHoverBed.bed_id];
    if (comp.foes.length) {
      triggerWarn('red', `${carry.crop.crop_name} clashes with ${comp.foes[0].name} in ${carryHoverBed.name}`, comp.foes[0].why || 'Keep these two in separate beds.');
    } else if (rv && rv.level === 'red') {
      triggerWarn('red', `Rotation clash in ${carryHoverBed.name}`, rv.reason);
    } else if (rv && rv.level === 'orange') {
      triggerWarn('amber', `A bit soon for ${carryHoverBed.name}`, rv.reason);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverBedId, carryCropId]);

  const StripChip = ({ c }) => {
    const st = cropSeason(c.crop_id);
    const base = colorFor(c);
    return (
      <div onPointerDown={(e) => startCarry(c, e)}
        data-testid={`palette-crop-${c.planting_id}`}
        className="flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full cursor-grab active:cursor-grabbing text-white text-sm font-bold shadow-sm select-none touch-none flex-shrink-0 transition-transform active:scale-95"
        style={{
          backgroundColor: base,
          opacity: st === 'out' ? 0.5 : 1,
          boxShadow: st === 'out' ? 'none' : `0 0 0 2px ${SEASON_GLOW[st]}66, 0 4px 12px -4px ${base}99`,
        }}
        title={st === 'peak' ? 'Peak season' : st === 'shoulder' ? 'Shoulder season' : 'Out of season'}>
        <span className="flex items-center justify-center rounded-full" style={{ width: 20, height: 20, background: 'rgba(255,255,255,0.28)' }}>
          <Sprout className="w-3 h-3 text-white" />
        </span>
        {c.crop_name}
      </div>
    );
  };

  return (
    <div className="relative" data-testid="planner-stage">
      {/* ---------- Bed canvas (full width, borderless) ---------- */}
      <div ref={canvasRef} data-testid="planner-canvas"
        onPointerDown={onCanvasPointerDown} onPointerMove={onCanvasPointerMove} onPointerUp={onCanvasPointerUp}
        className="relative w-full rounded-[32px] overflow-hidden"
        style={{
          aspectRatio: '16 / 10',
          background: 'radial-gradient(#1A433112 1.3px, transparent 1.3px) 0 0 / 28px 28px, linear-gradient(160deg, #FAF9F6 0%, #EEF3E7 100%)',
          touchAction: 'none', cursor: tool === 'draw' ? 'crosshair' : 'default',
          boxShadow: 'inset 0 0 120px rgba(26,67,49,0.06)',
        }}>
        {/* Beds */}
        {beds.map((bed, idx) => (
          <BedShape key={bed.bed_id} bed={bed} layout={currentLayout(bed, idx)}
            selected={selectedBedId === bed.bed_id} hovered={hoverBedId === bed.bed_id}
            verdictColor={carry && rotCheck[bed.bed_id] ? ROT_COLOR[rotCheck[bed.bed_id].level] : '#6B9F4A'}
            tool={tool} onBedPointerDown={onBedPointerDown} registerRef={registerRef} />
        ))}

        {/* Live sketch trail */}
        {stroke && stroke.length > 1 && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" data-testid="draw-stroke">
            <path d={strokePath(stroke)} fill="none" stroke="#1A4331" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 8" opacity="0.75" />
          </svg>
        )}

        {/* Draw hint */}
        {tool === 'draw' && (
          <div className="absolute z-20 top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-[#1A4331] text-white rounded-full px-4 py-2 shadow-lg" data-testid="draw-hint">
            <Sparkles className="w-3.5 h-3.5 text-[#B4D330]" />
            <span className="text-[11px] font-semibold">Sketch any shape — it snaps into a clean bed</span>
          </div>
        )}

        {/* Empty state */}
        {beds.length === 0 && !stroke && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
            <div className="w-16 h-16 rounded-3xl flex items-center justify-center mb-3" style={{ background: '#B4D33022' }}>
              <Pencil className="w-8 h-8" style={{ color: '#6B9F4A' }} />
            </div>
            <p className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>Lay out your first bed</p>
            <p className="text-sm text-[#7A6B57] mt-1 max-w-xs">Use <span className="font-semibold">Add bed</span> for an instant plot, or <span className="font-semibold">Draw</span> to sketch any shape.</p>
          </div>
        )}
      </div>

      {/* ---------- Carry ghost ---------- */}
      {carry && (
        <div className="fixed z-50 pointer-events-none -translate-x-1/2 -translate-y-1/2 px-3 py-2 rounded-full text-white text-sm font-bold shadow-xl"
          style={{ left: carry.x, top: carry.y, backgroundColor: colorFor(carry.crop) }}>
          {carry.crop.crop_name}
        </div>
      )}

      {/* ---------- Strategic advice / bed detail (slide-in) ---------- */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            data-testid="advice-card"
            className="absolute z-30 top-4 right-4 w-72 max-w-[86%] bg-white/80 backdrop-blur-xl rounded-3xl p-4 shadow-[0_20px_50px_-20px_rgba(26,67,49,0.5)] border border-white/60 max-h-[calc(100%-7rem)] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: '#1A433114' }}>
                <Sparkles className="w-4 h-4 text-[#1A4331]" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#1A4331]" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>Strategic advice</p>
                <p className="text-[10px] text-[#7A6B57]">Rotation · Companions · Season</p>
              </div>
              {selectedBed && !carry && (
                <button onClick={() => setSelectedBedId(null)} data-testid="advice-close" className="ml-auto text-[#7A6B57] hover:text-[#1A4331]"><X className="w-4 h-4" /></button>
              )}
            </div>

            {carry ? (
              <CarryAdvice crop={carry.crop} hoverBed={carryHoverBed} rotVerdict={carryHoverBed ? rotCheck[carryHoverBed.bed_id] : null}
                companionMap={companionMap} season={season[carry.crop.crop_id]} nowMonth={nowMonth} />
            ) : selectedBed ? (
              <div>
                {renaming === selectedBed.bed_id ? (
                  <form onSubmit={(e) => { e.preventDefault(); const v = e.target.bedname.value.trim(); if (v) onRenameBed(selectedBed.bed_id, v); setRenaming(null); }} className="flex gap-1.5 mb-3">
                    <input name="bedname" defaultValue={selectedBed.name} data-testid="bed-rename-input" autoFocus
                      className="flex-1 px-2.5 py-1.5 bg-[#F0F2EB] rounded-lg outline-none text-sm" />
                    <button type="submit" data-testid="bed-rename-save" className="px-2.5 rounded-lg bg-[#1A4331] text-white"><Check className="w-4 h-4" /></button>
                  </form>
                ) : (
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => setRenaming(selectedBed.bed_id)} data-testid="bed-rename" className="text-base font-bold text-[#1A4331] hover:underline" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>{selectedBed.name}</button>
                    <button onClick={() => { onDeleteBed(selectedBed.bed_id); setSelectedBedId(null); }} data-testid={`bed-delete-${selectedBed.bed_id}`} className="text-[#C66B4A]/70 hover:text-[#C66B4A] p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}

                <BedSizeBlock bed={selectedBed} onSave={(v) => onUpdateBedSize && onUpdateBedSize(selectedBed.bed_id, v)} />

                <button onClick={() => navigate(`/designer/${selectedBed.bed_id}`)} data-testid={`bed-design-${selectedBed.bed_id}`}
                  className="w-full flex items-center justify-center gap-2 rounded-2xl py-2.5 mb-2 font-semibold text-sm hg-press"
                  style={{ background: '#1A4331', color: '#FAF6EC' }}>
                  <LayoutGrid className="w-4 h-4" style={{ color: '#B4D330' }} /> Design this bed
                </button>

                <div className="flex gap-2 mb-3">
                  <button
                    onClick={async () => {
                      setFillMsg(null); setFilling(true);
                      try { const r = await onAutoFill?.(selectedBed.bed_id); setFillMsg(r?.summary || 'Bed filled.'); }
                      finally { setFilling(false); }
                    }}
                    disabled={filling}
                    data-testid={`bed-autofill-${selectedBed.bed_id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-2xl py-2.5 font-semibold text-sm hg-press disabled:opacity-60"
                    style={{ background: '#B4D330', color: '#1A4331' }}>
                    <Wand2 className="w-4 h-4" /> {filling ? 'Filling…' : 'Auto-fill bed'}
                  </button>
                  {(selectedBed.crops || []).length > 0 && (
                    <button
                      onClick={() => { if (window.confirm('Clear every crop from this bed? This removes them from your garden.')) { setFillMsg(null); onClearBed?.(selectedBed.bed_id); } }}
                      data-testid={`bed-clear-${selectedBed.bed_id}`}
                      className="flex items-center justify-center gap-1.5 rounded-2xl py-2.5 px-3 font-semibold text-sm hg-press"
                      style={{ background: '#FAF6EC', color: '#C66B4A', border: '1px solid rgba(198,107,74,0.35)' }}>
                      <Eraser className="w-4 h-4" /> Clear
                    </button>
                  )}
                </div>
                {fillMsg && (
                  <div data-testid="bed-autofill-result" className="text-[11px] text-[#1A4331] bg-[#B4D330]/15 rounded-xl px-3 py-2 mb-3 flex items-start gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#4E7A33] flex-shrink-0 mt-0.5" /><span>{fillMsg}</span>
                  </div>
                )}

                {(selectedBed.crops || []).length === 0 ? (
                  <p className="text-xs text-[#7A6B57] mb-3">Empty bed — tap a crop in the season strip, or drag one here.</p>
                ) : (
                  <div className="space-y-1.5 mb-3">
                    {selectedBed.crops.map((c) => (
                      <div key={c.planting_id} className="rounded-xl bg-[#F7F8F3] p-2">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colorFor(c) }} />
                          <span className="text-sm font-medium text-[#2D241E] flex-1 truncate">{c.crop_name}</span>
                          <span className="text-[10px] text-[#7A6B57]">×{c.plant_count || 1}</span>
                          <button onClick={() => onRemoveCrop(c.planting_id)} data-testid={`canvas-remove-${c.planting_id}`} className="text-[10px] text-[#7A6B57] hover:text-[#C66B4A]">Remove</button>
                        </div>
                        {successionCrops && successionCrops[c.crop_id] && (
                          <>
                            <button onClick={() => setOpenSucc(openSucc === c.planting_id ? null : c.planting_id)} data-testid={`succession-toggle-${c.planting_id}`}
                              className="text-[10px] font-semibold text-[#C9A85B] hover:text-[#a98a45] flex items-center gap-1 mt-1">
                              <Repeat className="w-3 h-3" /> {openSucc === c.planting_id ? 'Hide continuous harvest' : 'Continuous harvest'}
                            </button>
                            {openSucc === c.planting_id && <ContinuousHarvestPanel cropId={c.crop_id} cropName={c.crop_name} plantingId={c.planting_id} initialAiOn={aiOn} />}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <SuccessionTimeline bedId={selectedBed.bed_id} crops={selectedBed.crops} />

                {rotB?.best_next && (
                  <div className="rounded-2xl p-3 mb-2" style={{ background: '#6B9F4A12' }} data-testid="detail-rotation">
                    <div className="flex items-center gap-2 mb-1"><Repeat className="w-4 h-4 text-[#6B9F4A]" /><span className="text-xs font-bold text-[#1A4331]">Best next: {rotB.best_next.group_label}</span></div>
                    <p className="text-[11px] text-[#5C665C] leading-snug">{rotB.best_next.reason}</p>
                  </div>
                )}
                {rotB?.soil_health && (
                  <div className="flex items-start gap-1.5 text-[11px] leading-snug px-1" data-testid="detail-soil">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: rotB.soil_health.level === 'alert' ? '#C66B4A' : rotB.soil_health.level === 'warning' ? '#C9A85B' : '#6B9F4A' }} />
                    <span className="text-[#5C665C]"><b className="text-[#1A4331]">Soil:</b> {rotB.soil_health.reason}</span>
                  </div>
                )}
                {onOpenHistory && (
                  <button onClick={() => onOpenHistory(selectedBed)} data-testid={`bed-history-${selectedBed.bed_id}`}
                    className="text-[11px] font-semibold text-[#a98a45] hover:underline mt-1.5 px-1">Edit rotation history</button>
                )}

                {(selectedBed.bad_pairs?.length > 0 || selectedBed.good_pairs?.length > 0) && (
                  <div className="mt-2 space-y-1.5">
                    {selectedBed.bad_pairs?.map((p, i) => (
                      <div key={`b${i}`} className="flex items-start gap-1.5 text-[11px] text-[#2A3528] bg-[#C66B4A]/8 rounded-lg p-2"><AlertTriangle className="w-3.5 h-3.5 text-[#C66B4A] flex-shrink-0 mt-0.5" /><span>{p.note}</span></div>
                    ))}
                    {selectedBed.good_pairs?.map((p, i) => (
                      <div key={`g${i}`} className="flex items-start gap-1.5 text-[11px] text-[#2A3528] bg-[#6B9F4A]/8 rounded-lg p-2"><Heart className="w-3.5 h-3.5 text-[#6B9F4A] flex-shrink-0 mt-0.5" /><span>{p.note}</span></div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Bottom slot: biological warning (priority) or season strip ---------- */}
      <AnimatePresence mode="wait">
        {warn ? (
          <motion.div key="warn"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 360, damping: 30 }}
            data-testid="bio-warning-strip"
            className="absolute z-40 left-4 right-[84px] bottom-4 rounded-2xl px-4 py-3 flex items-start gap-3"
            style={{
              background: 'rgba(255,253,247,0.9)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              border: `1.5px solid ${warn.level === 'red' ? '#C66B4A' : '#C9A85B'}`,
              boxShadow: `0 0 0 4px ${warn.level === 'red' ? '#C66B4A' : '#C9A85B'}22, 0 16px 40px -16px ${warn.level === 'red' ? '#C66B4A' : '#C9A85B'}88`,
            }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: warn.level === 'red' ? '#C66B4A' : '#C9A85B' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-snug" style={{ color: warn.level === 'red' ? '#A84E31' : '#9A7A24' }} data-testid="bio-warning-title">{warn.title}</p>
              <p className="text-xs text-[#5C665C] leading-snug mt-0.5" data-testid="bio-warning-fix">{warn.fix}</p>
            </div>
            <button onClick={() => { if (warnTimer.current) clearTimeout(warnTimer.current); setWarn(null); }} data-testid="bio-warning-close" className="text-[#7A6B57] hover:text-[#1A4331]"><X className="w-4 h-4" /></button>
          </motion.div>
        ) : seasonOpen ? (
          <motion.div key="season"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            data-testid="season-strip"
            className="absolute z-20 left-4 right-[84px] bottom-4 rounded-3xl p-3"
            style={{ background: 'rgba(255,255,255,0.68)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 16px 40px -20px rgba(26,67,49,0.4)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#1A4331]">In season</span>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-[#7A6B57]"><span className="w-2 h-2 rounded-full" style={{ background: SEASON_GLOW.peak }} />Peak</span>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-[#7A6B57]"><span className="w-2 h-2 rounded-full" style={{ background: SEASON_GLOW.shoulder }} />Shoulder</span>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-[#7A6B57]"><span className="w-2 h-2 rounded-full" style={{ background: SEASON_GLOW.out }} />Off</span>
              <div className="relative ml-auto">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8EAA97]" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search crops…" data-testid="crop-search-input"
                  className="w-32 sm:w-44 pl-8 pr-3 py-1.5 bg-[#F0F2EB]/80 rounded-full outline-none text-xs focus:ring-2 focus:ring-[#8EAA97]/30" />
              </div>
            </div>
            {sortedStrip.length === 0 ? (
              <p className="text-xs text-[#7A6B57] px-1 py-1.5" data-testid="palette-empty">
                {unassigned.length === 0 ? 'Every crop is placed. Sow more from the Crop Library to see them here.' : 'No crops match that search.'}
              </p>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                {sortedStrip.map((c) => <StripChip key={c.planting_id} c={c} />)}
              </div>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* ---------- Floating control cluster (bottom-right) ---------- */}
      <div className="absolute z-40 bottom-4 right-4 flex flex-col gap-2" data-testid="planner-controls">
        <ClusterBtn icon={Plus} label="Add bed" onClick={quickAddBed} active={false} testid="ctrl-add-bed" />
        <ClusterBtn icon={Pencil} label="Draw" onClick={() => setTool(tool === 'draw' ? 'select' : 'draw')} active={tool === 'draw'} testid="tool-draw" />
        <ClusterBtn icon={Wand2} label="Season" onClick={() => setSeasonOpen((o) => !o)} active={seasonOpen} testid="ctrl-season" />
      </div>
    </div>
  );
}


// P2.3 — Succession-wave timeline: staggered replant batches for the bed's
// succession-friendly crops, surfaced as a compact month-anchored strip.
function SuccessionTimeline({ bedId, crops }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const cropKey = (crops || []).map((c) => c.crop_id).sort().join(',');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/beds/${bedId}/succession`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [bedId, cropKey]);

  if (loading || !data || !data.has_waves) return null;
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }); } catch { return ''; } };

  return (
    <div className="mt-3 rounded-2xl p-3" style={{ background: '#C9A85B14', border: '1px solid rgba(201,168,91,0.25)' }} data-testid={`succession-timeline-${bedId}`}>
      <div className="flex items-center gap-2 mb-2">
        <Repeat className="w-4 h-4 text-[#C9A85B]" />
        <span className="text-xs font-bold text-[#1A4331]">Succession waves</span>
        <span className="text-[10px] text-[#7A6B57]">keep a steady harvest</span>
      </div>
      <div className="space-y-2.5">
        {data.waves.map((w) => (
          <div key={w.crop_id} data-testid={`succession-wave-${w.crop_id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold text-[#2D241E]">{w.crop_name}</span>
              <span className="text-[10px] text-[#7A6B57]">every {w.interval_weeks} wks · {w.batch_size_hint}</span>
            </div>
            {w.window_open && w.schedule?.length ? (
              <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                {w.schedule.map((b, i) => (
                  <div key={i} className="flex-shrink-0 rounded-lg px-2 py-1 text-center" style={{ background: i === 0 ? '#6B9F4A' : '#FAF6EC', border: '1px solid rgba(26,67,49,0.12)', minWidth: 58 }}>
                    <div className="text-[10px] font-bold leading-tight" style={{ color: i === 0 ? '#FAF6EC' : '#1A4331' }}>{i === 0 ? 'Sow now' : fmt(b.date)}</div>
                    <div className="text-[9px] leading-tight flex items-center justify-center gap-0.5" style={{ color: i === 0 ? '#E5EBDD' : '#7A6B57' }}><Scissors className="w-2.5 h-2.5" />{fmt(b.harvest_date)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-[#7A6B57] italic">{w.stop_reason || 'Window closed for the season.'}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
